/* eslint-disable max-statements */
import { cacheDecorator } from '@ds/swymkit/tools'
import { defineStore } from 'pinia'

import { ERROR_LEGACY_DM_KO } from '~/config/conversation'

import type { CreateCommunityData } from '~/entities/container/community/interfaces/community'
import type { IConversation } from '~/entities/container/conversation/interfaces/conversation'
import { useConversationsStoreV1 } from '~/entities/container/conversation/stores/v1-conversations'
import { useConversationsStoreV2 } from '~/entities/container/conversation/stores/v2-conversations'
import type { IUser } from '~/entities/user/user/interfaces/user'
import { useUsersStore } from '~/entities/user/user/stores/users'

import { useConversationsAURAStore } from '~/features-swym/aura/stores/conversations-aura'

import { useFavoritesStore } from '~/features-shared/favorites/stores/favorites'
import type { SUBJECT_6W_URI } from '~/features-shared/libs/interfaces/entity'
import { useRTInterfaceResponseHandler } from '~/features-shared/rtconv/api/rtinterface-response-handler'

import { useFeatureManager } from '~/features-generic/config-manager/composables/feature-manager'

export const useConversationsStore = defineStore('conversations', () => {
  const storeConversationsV2 = useConversationsStoreV2() // RTC
  const storeAIConversations = useConversationsAURAStore()
  const storeConversationsV1 = useConversationsStoreV1() // Swym

  const storeFavorites = useFavoritesStore()
  const storeUsers = useUsersStore()
  const isRTCActivated = useFeatureManager('conversation.rtc')
  useRTInterfaceResponseHandler()

  watch(isRTCActivated, () => {
    storeConversationsV2.fetchList()
    storeAIConversations.fetchList()
  })

  whenever(isRTCActivated, () => {
    // This should be called immediately because if the value of flag is true by default, then the watcher is not triggered
    storeConversationsV2.fetchMaxAllowedUsers?.()
  }, { immediate: true })

  // #region STATES
  const threads = computed(() => new Map([...storeConversationsV1.threads].concat([...storeConversationsV2.threads])))
  const threadsId = computed(() => storeConversationsV1.threadsId.concat(storeConversationsV2.threadsId))
  // #endregion

  // #region METHODS
  const getConversationById = (id: string) => {
    return storeConversationsV1.getConversationById(id)
      || storeConversationsV2.getConversationById(id)
  }

  const getConversationBySubjectUri = (subject_uri: SUBJECT_6W_URI) => {
    return storeConversationsV1.getConversationBySubjectUri(subject_uri)
      || storeConversationsV2.getConversationBySubjectUri(subject_uri)
  }

  const isConversationFromV2 = (id = '') =>
    id.split(':')[4] === 'rt' // id is subjectUri "swym:[cluster]:[platform]:community:rt:[id]" or "swym:[cluster]:[platform]:community:[id]"
    || id.startsWith('rt:') // id is an id "[id]" or "rt:[id]"
    || !Number.isNaN(Number(id)) // id is an id but rtconv id can always be parsed as number
    || storeConversationsV2.getConversationById(id) // as fallback, if the id is in the store, this is an rtconv

  const getSubjectUriFromId = (id: string) => {
    if (isConversationFromV2(id))
      return storeConversationsV2.getSubjectUriFromId(id)
    else
      return storeConversationsV1.getSubjectUriFromId(id)
  }

  const getConversationTitle = (id: string, user_list?: boolean) => {
    if (isConversationFromV2(id))
      return storeConversationsV2.getConversationTitle(id, user_list)
    else
      return storeConversationsV1.getConversationTitle(id, user_list)
  }

  const getConversationTime = (el: IConversation) => {
    if (isConversationFromV2(el.id))
      return storeConversationsV2.getConversationTime(el)
    else
      return storeConversationsV1.getConversationTime(el)
  }
  /** Is conversation unread */
  function isConversationUnread(el: IConversation): boolean {
    return isConversationFromV2(el?.id)
      ? storeConversationsV2.isConversationUnread(el)
      : storeConversationsV1.isConversationUnread(el)
  }
  // #endregion

  // #region GETTERS
  const countUnread = computed(() => storeConversationsV1.countUnread + storeConversationsV2.countUnread)
  const threadsVisible = computed(() => storeConversationsV1.threadsVisible.concat(storeConversationsV2.threadsVisible))
  const countMissedCalls = computed(() => storeConversationsV2.countMissedCalls)
  const maxAllowedUsers = computed(() => {
    if (isRTCActivated.value)
      return storeConversationsV2.maxAllowedUsers
    return storeConversationsV1.maxAllowedUsers
  })
  // #endregion

  // #region ACTIONS
  const doFilter = (filter: string) => {
    if (isRTCActivated.value) {
      return Promise.allSettled([storeConversationsV2.doFilter(filter), storeConversationsV1.doFilter(filter)])
        .then((results) => {
          const filteredThreads: string[] = []
          for (const result of results) {
            if (result?.status === 'fulfilled' && result.value)
              filteredThreads.concat(result.value)
          }
          return filteredThreads
        })
    }
    else { return storeConversationsV1.doFilter(filter) }
  }
  const fetchList = () => {
    if (isRTCActivated.value)
      storeConversationsV2.fetchList()
    return storeConversationsV1.fetchList()
  }
  const fetchConversation = (id: string) => {
    if (isConversationFromV2(id)) { return storeConversationsV2.fetchConversation(id) }
    else {
      return storeConversationsV1.fetchConversation(id)
        .catch((error) => {
          return storeConversationsV2.fetchRTCIdFromLegacyConversation(id)
            .then((rtcId) => {
              if (rtcId)
                throw new Error(ERROR_LEGACY_DM_KO, { cause: { rtcId } })

              else
                throw error
            })
        })
    }
  }
  const doCreateConversation = async (userLogins: string[], title?: string): Promise<IConversation> => {
    // users may or may not contain the current user login, so add the current user always
    // to always have a consistent value for checks
    // front end check is currently needed before creating 1:1 rtconv
    // because rtconv cannot check if conv exists in swym today
    const currentUserLogin = storeUsers.currentUser?.login
    const otherUserLogin = userLogins.find(u => u !== currentUserLogin)
    const userLoginsCopy = [...userLogins] // create a temp copy for modification

    if (!otherUserLogin || !currentUserLogin)
      throw new Error('Cannot create a conversation without members')

    // add current user to user array if not present and do the checks accordingly
    if (!userLogins.includes(currentUserLogin))
      userLoginsCopy.push(currentUserLogin)

    if (isRTCActivated.value) {
      if (userLoginsCopy.length > 2) // not a 1:1 conversation
        return storeConversationsV2.doCreateConversation(userLoginsCopy, title)
      const conversation = await storeConversationsV1.getConversationIfExists(otherUserLogin, true)
      if (conversation)
        return conversation
      else return storeConversationsV2.doCreateConversation(userLoginsCopy, title)
    }
    else {
      return storeConversationsV1.doCreateConversation(userLoginsCopy, title)
    }
  }
  const getMembers = (
    uri: string,
    page = 1,
    search?: string,
    refine?: string[],
  ) => {
    if (isConversationFromV2(uri)) {
      return storeConversationsV2.getMembers(
        uri,
        page,
        search,
        // refine,
      )
    }
    else {
      return storeConversationsV1.getMembers(
        uri.split(':')
          .pop()!,
        page,
        search,
        refine,
      )
    }
  }
  const doAddMembersToConversation = (id: string, users: string[]) => {
    if (isConversationFromV2(id))
      return storeConversationsV2.doAddMembersToConversation(id, users)
    else
      return storeConversationsV1.doAddMembersToConversation(id, users)
  }
  const doRemoveMemberFromConversation = (id: string, user: IUser) => {
    if (isConversationFromV2(id))
      return storeConversationsV2.doRemoveMemberFromConversation(id, user)
    else
      return storeConversationsV1.doRemoveMemberFromConversation(id, user)
  }
  const markAsRead = (id: string) => {
    if (isConversationFromV2(id))
      return storeConversationsV2.markAsRead(id)
    else
      return storeConversationsV1.markAsRead(id)
  }
  const hideConversation = (id: string) => {
    if (isConversationFromV2(id))
      return storeConversationsV2.hideConversation(id)
    else
      return storeConversationsV1.hideConversation(id)
  }
  const restoreConversation = (id: string) => {
    if (isConversationFromV2(id))
      return storeConversationsV2.hideConversation(id, false)
    else
      return storeConversationsV1.hideConversation(id, false)
  }
  const setTopic = (id: string, topic: string) => {
    if (isConversationFromV2(id))
      return storeConversationsV2.setTopic(id, topic)
    else
      return storeConversationsV1.setTopic(id, topic)
  }
  const setPersistency = (id: string, is_persistent: boolean) => {
    if (isConversationFromV2(id))
      return storeConversationsV2.setPersistency(id, !is_persistent)
    else
      return storeConversationsV1.setPersistency(id, is_persistent)
  }

  const transformDmToCommunity = (data: CreateCommunityData, conversation: IConversation) => {
    if (isConversationFromV2(conversation.id))
      return storeConversationsV2.transformConvToCommunity(data, conversation)
    else
      return storeConversationsV1.transformDmToCommunity(data, conversation)
  }

  const toggleMute = (thread: IConversation, state?: boolean) => {
    if (isConversationFromV2(thread.id))
      return storeConversationsV2.toggleMute(thread.subject_uri, state)
  }
  // #endregion

  // #region FAVORITES
  const favorites = computed(() => {
    const dmFav = Array.from(
      storeFavorites.get('dm'),
      ([subject_uri, position]) => ({ subject_uri, position, type: 'dm' }),
    )

    const externaldmFav = Array.from(
      storeFavorites.get('externaldm'),
      ([subject_uri, position]) => ({ subject_uri, position, type: 'externaldm' }),
    )
    const fav = dmFav.concat(externaldmFav)
      .sort((a, b) => {
        if (a.position === b.position) {
          if (a.type === b.type)
            return 0
          if (a.type === 'externaldm')
            return -1
          if (b.type === 'externaldm')
            return 1
          return 0
        }
        return a.position - b.position
      })
    return new Map(fav.map(el => [el.subject_uri, 1 + fav.indexOf(el)]))
  })

  const deleteFavorite = (subject_uri: SUBJECT_6W_URI) => {
    if (isConversationFromV2(subject_uri.split(':')
      .pop() || subject_uri)) {
      return storeFavorites.remove('externaldm', subject_uri)
    }
    else {
      return storeFavorites.remove('dm', subject_uri)
    }
  }

  const addFavorite = (subject_uri: SUBJECT_6W_URI, position: number) => {
    if (isConversationFromV2(subject_uri.split(':')
      .pop() || subject_uri)) {
      return storeFavorites.add(
        'externaldm',
        subject_uri,
        position,
      )
    }
    else {
      return storeFavorites.add(
        'dm',
        subject_uri,
        position,
      )
    }
  }
  // #endregion

  return {
    // #region STATES
    threads,
    threadsId,
    // #endregion

    // #region COMPUTED
    countUnread,
    threadsVisible,
    countMissedCalls,
    maxAllowedUsers,
    // #endregion

    // #region METHODS
    getConversationById,
    getConversationBySubjectUri,
    getConversationTitle,
    getConversationTime,
    isConversationFromV2,
    isConversationUnread,
    getSubjectUriFromId,
    // #endregion

    // #region ACTIONS
    doFilter,
    doCreateConversation,
    getMembers,
    doAddMembersToConversation,
    doRemoveMemberFromConversation,
    fetchList: () => cacheDecorator('fetch-conversations', fetchList, 60000),
    fetchConversation,
    markAsRead,
    hideConversation,
    restoreConversation,
    setTopic,
    setPersistency,
    transformDmToCommunity,
    resetMissedBadge: storeConversationsV2.resetMissedBadge,
    toggleMute,

    // #region FAVORITES
    favorites,
    deleteFavorite,
    addFavorite,
    // #endregion
  }
})
